import type { ReactNode } from "react";

import { cn } from "../lib/cn";

export interface RadialProgressProps {
  /** 0..1. Clamped, because a clutch can sit past its expected hatch day. */
  value: number;
  label: string;
  /**
   * Where the dial is drawn. `onSurface` is the accent mark on a pale track, for a
   * dial sitting on a card; `onAccent` inverts to ink on a translucent track, for the
   * hero dial that sits *on* the accent fill, where the accent mark would vanish.
   */
  tone?: "onSurface" | "onAccent";
  /** Ring thickness in the SVG's own 100-unit space. The design varies it by size. */
  strokeWidth?: number;
  /** Rendered in the middle of the ring — the day count, on the nest screen. */
  children?: ReactNode;
  className?: string;
}

const CENTRE = 50;

/**
 * Incubation as a dial rather than a bar.
 *
 * A clutch runs thirteen or fourteen days and the breeder checks it daily, so the
 * reading that matters is "how much is left", which a closed ring answers at a glance
 * and a bar does not. The figure in the middle is what is actually read; the ring is
 * the context around it.
 *
 * Drawn in a unitless view box and scaled by the container, so the same component is a
 * hero dial on the nest screen and an 84px thumbnail on the home screen without a size
 * prop. The radius follows the stroke so a thicker ring stays inside the box.
 */
export function RadialProgress({
  value,
  label,
  tone = "onSurface",
  strokeWidth = 12,
  children,
  className,
}: RadialProgressProps) {
  const clamped = Math.min(1, Math.max(0, value));
  const radius = CENTRE - strokeWidth / 2 - 1;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className={cn("relative isolate", className)}>
      <svg
        viewBox="0 0 100 100"
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.round(clamped * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="size-full -rotate-90"
      >
        <circle
          cx={CENTRE}
          cy={CENTRE}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className={tone === "onAccent" ? "stroke-on-primary-fixed/15" : "stroke-primary-fixed"}
        />
        <circle
          cx={CENTRE}
          cy={CENTRE}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped)}
          className={cn(
            "transition-[stroke-dashoffset] duration-500",
            tone === "onAccent" ? "stroke-on-primary-fixed-variant" : "stroke-primary",
          )}
        />
      </svg>

      {children ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
      ) : null}
    </div>
  );
}
